/**
 * Chatbot Component
 * 
 * This component provides an AI-powered chat interface using Firebase Functions
 * and Google's Gemini AI. The chatbot appears as a slide-in panel (Sheet) from
 * the right side of the screen.
 * 
 * How it works:
 * 1. User types a message and clicks send
 * 2. Message is sent to Firebase Functions (chatFunction)
 * 3. Firebase Function calls Gemini AI with the message
 * 4. AI response comes back and is displayed in the chat
 * 
 * Real-world analogy:
 * - User = Customer asking questions
 * - Chatbot = Customer service representative
 * - Firebase Functions = The phone system connecting them
 * - Gemini AI = The knowledge base the rep consults
 */

import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Loader2, Bot, User } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { chatFunction } from '@/lib/firebase';

export const Chatbot = ({ isOpen, onOpenChange }) => {
  // State Management
  // messages: Array of message objects with {text, sender: 'user'|'bot'}
  const [messages, setMessages] = useState([
    {
      text: "Hello! I'm your AI assistant for Sagar Ventures. How can I help you today?",
      sender: 'bot',
    },
  ]);
  
  // input: The current text in the input field
  const [input, setInput] = useState('');
  
  // isLoading: Whether we're waiting for the AI to respond
  const [isLoading, setIsLoading] = useState(false);
  
  // messagesEndRef: Reference to scroll to the bottom when new messages arrive
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  // useEffect runs code when dependencies change (in this case, messages)
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Function to scroll chat to the bottom
  // This ensures users always see the latest message
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Function to handle sending a message
  // This is called when user clicks send button or presses Enter
  const sendMessage = async (e) => {
    e.preventDefault(); // Prevent form submission (page refresh)
    
    // Don't send if input is empty or just whitespace
    if (!input.trim() || isLoading) return;

    // Create user message object
    const userMessage = { text: input.trim(), sender: 'user' };
    
    // Add user message to the messages array immediately
    // This gives instant feedback to the user
    setMessages((prev) => [...prev, userMessage]);
    
    // Clear the input field
    setInput('');
    
    // Set loading state to true (shows loading indicator)
    setIsLoading(true);

    try {
      // Call the Firebase Function with the user's message
      // chatFunction is imported from firebase.js
      // It's a "callable function" that securely communicates with Firebase Functions
      const result = await chatFunction({ message: userMessage.text });
      
      // Extract the AI's reply from the response
      // result.data contains the data returned from Firebase Function
      const botMessage = {
        text: result.data.reply,
        sender: 'bot',
      };
      
      // Add bot's response to messages
      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      // Handle errors gracefully
      console.error('Error sending message:', error);
      
      // Extract error message from Firebase HttpsError
      // Firebase HttpsError has the message in error.message
      // But sometimes the details are in error.details or error.code
      let errorText = 'Sorry, I encountered an error while chatting. Please try again.';
      
      if (error.message) {
        errorText = error.message;
      } else if (error.details) {
        errorText = error.details;
      } else if (typeof error === 'string') {
        errorText = error;
      }
      
      // Provide more helpful error messages based on error code
      if (error.code === 'functions/not-found') {
        errorText = 'Chat function not found. The Firebase Functions are not deployed or the emulator is not running.\n\n' +
                   'For local development:\n' +
                   '1. Run: firebase emulators:start --only functions\n' +
                   '2. Make sure REACT_APP_USE_FUNCTIONS_EMULATOR=true is in frontend/.env\n\n' +
                   'For production:\n' +
                   '1. Deploy: firebase deploy --only functions';
      } else if (error.code === 'functions/failed-precondition') {
        // Check if the error message contains API key info
        if (error.message && error.message.includes('API key')) {
          errorText = error.message + '\n\n' +
                     'Quick fix:\n' +
                     '1. Run: cd frontend/functions && node setup-chatbot.js\n' +
                     '2. Follow the setup instructions';
        } else {
          errorText = 'Chat service is not properly configured. The Gemini API key is missing.\n\n' +
                     'Quick fix:\n' +
                     '1. Run: cd frontend/functions && node setup-chatbot.js\n' +
                     '2. Follow the setup instructions';
        }
      } else if (error.code === 'functions/unavailable') {
        errorText = 'Chat service is temporarily unavailable. Please try again in a moment.';
      } else if (error.code === 'functions/deadline-exceeded') {
        errorText = 'The request took too long. Please try again with a shorter message.';
      }
      
      // Show user-friendly error message
      const errorMessage = {
        text: errorText,
        sender: 'bot',
      };
      
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      // Always set loading to false, even if there was an error
      setIsLoading(false);
      
      // Focus back on input field for better UX
      inputRef.current?.focus();
    }
  };

  // Focus input when sheet opens
  useEffect(() => {
    if (isOpen) {
      // Focus input after a short delay (allows Sheet animation to complete)
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isOpen]);

  return (
    <>
      {/* Chatbot Sheet - Slides in from the right */}
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
          {/* Header */}
          <SheetHeader className="px-6 py-4 border-b bg-gradient-to-r from-primary/10 to-primary/5">
            <SheetTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-primary" />
              <span>AI Assistant</span>
            </SheetTitle>
            <SheetDescription>
              Ask me anything about Sagar Ventures
            </SheetDescription>
          </SheetHeader>

          {/* Messages Container */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            <AnimatePresence>
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className={`flex gap-3 ${
                    message.sender === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  {/* Bot Avatar */}
                  {message.sender === 'bot' && (
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-primary" />
                    </div>
                  )}

                  {/* Message Bubble */}
                  <div
                    className={`max-w-[80%] rounded-lg px-4 py-2 ${
                      message.sender === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap break-words">
                      {message.text}
                    </p>
                  </div>

                  {/* User Avatar */}
                  {message.sender === 'user' && (
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-primary-foreground" />
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Loading Indicator */}
            {isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex gap-3 justify-start"
              >
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-primary" />
                </div>
                <div className="bg-muted rounded-lg px-4 py-2">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </div>
              </motion.div>
            )}

            {/* Scroll anchor - invisible element at bottom for auto-scroll */}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Form */}
          <form
            onSubmit={sendMessage}
            className="border-t px-4 py-4 bg-background"
          >
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                disabled={isLoading}
                className="flex-1"
                onKeyDown={(e) => {
                  // Allow sending with Enter key
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(e);
                  }
                }}
              />
              <Button
                type="submit"
                disabled={!input.trim() || isLoading}
                className="shrink-0"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

    </>
  );
};
